import { StrictMode, useEffect } from 'react'
import { createRoot } from 'react-dom/client'
import { AuthKitProvider } from '@workos-inc/authkit-react'
import { ExtendedAuthProvider } from './shared/auth/AuthContext'
import App from './App.tsx'
import './index.css'
import { initializeUrlTracking } from './shared/lib/tracking'
import { track } from './shared/analytics'
import { initSentry, Sentry } from './shared/lib/sentry'
import {
  canReloadForChunkError,
  isDynamicImportFailure,
  markChunkReload,
} from './shared/lib/chunkReload'

// Sentry first so boot-time errors in everything after this are captured.
initSentry()

// Initialize URL tracking as early as possible
initializeUrlTracking()

// Replay UTMs carried across the WorkOS round-trip via `state` (set by
// useStartTrial) back into Clarity so the post-signup session stays
// attributable to the original landing source.
function replayAuthState(state: unknown): void {
  if (!state || typeof state !== 'object') return
  const utm = (state as { utm?: unknown }).utm
  if (!utm || typeof utm !== 'object') return
  void track('signup_completed', utm as Record<string, unknown>)
}

// Stale route chunk after a deploy: reload once to fetch the new bundle.
// Throttled via sessionStorage so a genuinely broken deploy can't loop.
function ErrorFallback({ error }: { error: unknown }) {
  const willReload = isDynamicImportFailure(error) && canReloadForChunkError()
  useEffect(() => {
    if (!willReload) return
    markChunkReload()
    window.location.reload()
  }, [willReload])
  if (willReload) return null
  return <p>Something went wrong. Please reload.</p>
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={(props) => <ErrorFallback error={props.error} />}>
      {/* devMode=true keeps the refresh token in localStorage + request body, avoiding the 3P cookie on api.workos.com that browsers drop without a WorkOS custom domain. */}
      <AuthKitProvider
        clientId={import.meta.env.VITE_WORKOS_CLIENT_ID}
        redirectUri={import.meta.env.VITE_WORKOS_REDIRECT_URI}
        devMode={true}
        onRedirectCallback={({ state }) => replayAuthState(state)}
        onRefreshFailure={() => {
          // authkit-js swallows RefreshError internally and only exposes it here,
          // so without this hook the 400s never reach Sentry's global handlers.
          Sentry.captureMessage('authkit refresh failed', {
            level: 'warning',
            tags: { area: 'authkit-refresh' },
          })
        }}
      >
        <ExtendedAuthProvider>
          <App />
        </ExtendedAuthProvider>
      </AuthKitProvider>
    </Sentry.ErrorBoundary>
  </StrictMode>,
)
